import React, { useRef, useState } from "react";
import { theme } from "../theme.js";
import { API_URL } from "../utils/constants.js";

export default function FileUploadGPX({ onGPXUpload, onGPXChange, eventId }) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [error, setError] = useState(null);

  const handleFilePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    setError(null);

    if (!file) {
      setSelectedFile(null);
      onGPXChange(null);
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith(".gpx")) {
      setError("Seul les fichiers GPX sont acceptés");
      return;
    }

    setSelectedFile(file);
    onGPXChange(file);

    // Auto-upload GPX file
    await uploadGPX(file);
  };

  const uploadGPX = async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("event_id", eventId || "");

      const res = await fetch(`${API_URL}/api/races/upload-gpx`, {
        method: "POST",
        body: fd,
      });

      const json = await res.json().catch(() => null);

      if (res.ok && json) {
        setUploadedData(json);
        onGPXUpload && onGPXUpload(json);
        setError(null);
      } else {
        setError(json?.error || "Erreur lors du téléversement du fichier GPX");
        setUploadedData(null);
      }
      return json;
    } catch (err) {
      console.error("FileUploadGPX error:", err);
      setError("Erreur de connexion lors du téléversement");
      setUploadedData(null);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setUploadedData(null);
    setError(null);
    onGPXChange(null);
    onGPXUpload && onGPXUpload(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Fichier de parcours GPX</h3>
        <p style={styles.description}>
          Téléchargez un fichier GPX contenant le parcours de la course
        </p>
      </div>

      <div style={styles.uploadArea}>
        <input
          ref={inputRef}
          type="file"
          accept=".gpx"
          onChange={handleFilePick}
          style={styles.hiddenInput}
        />

        <div style={styles.controls}>
          {!selectedFile && (
            <button
              type="button"
              onClick={() => inputRef.current && inputRef.current.click()}
              style={styles.pickButton}
            >
              Choisir un fichier GPX
            </button>
          )}

          {selectedFile && (
            <div style={styles.fileInfo}>
              <span style={styles.fileName}>{selectedFile.name}</span>
              <button
                type="button"
                onClick={handleRemove}
                style={styles.removeButton}
              >
                X
              </button>
            </div>
          )}

          {uploading && <span style={styles.uploading}>Traitement...</span>}

          {uploadedData && (
            <div style={styles.uploadedInfo}>
              <span style={styles.uploaded}>OK</span>
              <div style={styles.trackDetails}>
                {uploadedData.title && (
                  <p style={styles.detail}>
                    <strong>Titre:</strong> {uploadedData.title}
                  </p>
                )}
                {uploadedData.distance_m && (
                  <p style={styles.detail}>
                    <strong>Distance:</strong>{" "}
                    {(uploadedData.distance_m / 1000).toFixed(2)} km
                  </p>
                )}
                {uploadedData.elevation_gain_m && (
                  <p style={styles.detail}>
                    <strong>Dénivelé +:</strong> {uploadedData.elevation_gain_m.toFixed(0)} m
                  </p>
                )}
                {uploadedData.duration_s && (
                  <p style={styles.detail}>
                    <strong>Durée estimée:</strong>{" "}
                    {Math.round(uploadedData.duration_s / 60)} min
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {error && <div style={styles.errorMessage}>{error}</div>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: theme.spacing.lg,
    backgroundColor: "#f9fafb",
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
    zIndex: 0,
    width: "100%",
  },
  header: {
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
    margin: 0,
    marginBottom: theme.spacing.sm,
  },
  description: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    margin: 0,
  },
  uploadArea: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.md,
  },
  hiddenInput: { display: "none" },
  controls: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing.md,
  },
  pickButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    transition: theme.transitions.base,
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    width: "100%",
  },
  fileName: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  removeButton: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    background: theme.colors.error,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.sm,
  },
  uploading: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.primary,
    fontStyle: "italic",
  },
  uploadedInfo: {
    width: "100%",
    padding: theme.spacing.md,
    backgroundColor: "#ecfdf5",
    border: `1px solid #10b981`,
    borderRadius: theme.radius.md,
  },
  uploaded: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.success,
    fontWeight: theme.fonts.weight.semibold,
  },
  trackDetails: {
    marginTop: theme.spacing.md,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing.md,
    zIndex: 0,
  },
  detail: {
    fontSize: theme.fonts.size.sm,
    color: "#065f46",
    margin: 0,
    padding: theme.spacing.xs,
  },
  errorMessage: {
    padding: theme.spacing.md,
    backgroundColor: "#fef2f2",
    border: `1px solid ${theme.colors.error}`,
    borderRadius: theme.radius.md,
    color: theme.colors.error,
    fontSize: theme.fonts.size.sm,
  },
};
